import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import styles from "./CoolAssistantWidget.module.css";
import { getClassName } from "../../utils/className.js";
import IconButton from "../IconButton/IconButton.jsx";
import SearchInput from "../SearchInput/SearchInput.jsx";
import Checkbox from "../Checkbox/Checkbox.jsx";
import ChatBubbleIcon from "../../Icons/ChatBubbleIcon.jsx";
import ChevronDown from "../../Icons/ChevronDown.jsx";
import ChevronLeft from "../../Icons/ChevronLeft.jsx";
import CloseIcon from "../../Icons/CloseIcon.jsx";
import CoolAssistantLogo from "../../Icons/CoolAssistantLogo.jsx";
import CoolAssistantMark from "../../Icons/CoolAssistantMark.jsx";
import CoolAssistantMarkLight from "../../Icons/CoolAssistantMarkLight.jsx";
import EditIcon from "../../Icons/EditIcon.jsx";
import FileIcon from "../../Icons/FileIcon.jsx";
import MenuIcon from "../../Icons/MenuIcon.jsx";
import SendIcon from "../../Icons/SendIcon.jsx";

let nextId = 1;
const STICK_TO_BOTTOM_THRESHOLD = 40;
// Below this many sites the picker's search box is just noise.
const SITE_SEARCH_MIN_SITES = 8;
const IMAGE_EXTENSION_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;
const HTML_EXTENSION_RE = /\.html?$/i;

const toPx = (value) => (typeof value === "number" ? `${value}px` : undefined);

const SanitizedHtml = ({ html }) => {
  const sanitized = useMemo(() => DOMPurify.sanitize(html), [html]);
  if (!sanitized) {
    return null;
  }
  return (
    <div
      className={styles.messageHtml}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

const FileAttachments = ({ files }) => {
  if (!files?.length) {
    return null;
  }
  return (
    <div className={styles.fileAttachments}>
      {files.map((file, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <FileAttachment key={file?.link ?? index} file={file} />
      ))}
    </div>
  );
};

const FileAttachment = ({ file }) => {
  // The `download` attribute only forces a save for same-origin links (or if the server
  // sends Content-Disposition: attachment); a cross-origin file link — the common case for
  // signed cloud-storage URLs — makes browsers ignore it and just navigate/open the file
  // instead. Fetching the file ourselves and saving it via a blob URL works regardless of
  // the resource's origin, as long as it's CORS-readable; if the fetch fails (no CORS,
  // network error), fall back to opening the link directly.
  const handleFileClick = useCallback(
    async (e) => {
      if (!file?.link) {
        return;
      }
      e.preventDefault();
      try {
        const response = await fetch(file.link);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = file.name || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
      } catch (err) {
        window.open(file.link, "_blank", "noopener,noreferrer");
      }
    },
    [file?.link, file?.name],
  );

  if (!file?.link) {
    return null;
  }

  if (IMAGE_EXTENSION_RE.test(file.name || file.link)) {
    return (
      <a
        href={file.link}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.fileImageLink}
      >
        <img
          src={file.link}
          alt={file.name || "attachment"}
          className={styles.fileImage}
        />
      </a>
    );
  }

  if (HTML_EXTENSION_RE.test(file.name || file.link)) {
    return (
      <iframe
        src={file.link}
        title={file.name || "chart"}
        className={styles.fileIframe}
        sandbox="allow-scripts"
      />
    );
  }

  return (
    <a
      href={file.link}
      target="_blank"
      rel="noopener noreferrer"
      download={file.name}
      onClick={handleFileClick}
      className={styles.fileCard}
    >
      <FileIcon size={28} color="#0b222d" />
      <span className={styles.fileInfo}>
        <span className={styles.fileName}>{file.name}</span>
        {file.size && <span className={styles.fileSize}>{file.size}</span>}
      </span>
    </a>
  );
};

const renderFormattedInlineText = (str) => {
  if (!str || typeof str !== "string" || !str.includes("**")) {
    return str;
  }
  const parts = str.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
      // eslint-disable-next-line react/no-array-index-key
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const isSeparatorRow = (cells) =>
  cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell.trim()));

const parseTableCell = (line) => {
  let raw = line.trim();
  if (raw.startsWith("|")) raw = raw.slice(1);
  if (raw.endsWith("|")) raw = raw.slice(0, -1);
  return raw.split("|").map((cell) => cell.trim());
};

const isTableLine = (line) => {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return false;
  const cells = parseTableCell(trimmed);
  return cells.length >= 2;
};

const parseMessageText = (text) => {
  if (!text) return [];
  const lines = text.split("\n");
  const blocks = [];
  let currentTable = null;
  let currentTextLines = [];

  const flushText = () => {
    if (currentTextLines.length > 0) {
      blocks.push({ type: "text", content: currentTextLines.join("\n") });
      currentTextLines = [];
    }
  };

  const flushTable = () => {
    if (currentTable) {
      if (currentTable.length >= 2) {
        const parsedRows = currentTable.map(parseTableCell);
        let headers = [];
        let rows = [];

        if (parsedRows.length > 1 && isSeparatorRow(parsedRows[1])) {
          headers = parsedRows[0];
          rows = parsedRows.slice(2);
        } else {
          headers = parsedRows[0];
          rows = parsedRows.slice(1);
        }
        blocks.push({ type: "table", headers, rows });
      } else {
        currentTextLines.push(...currentTable);
      }
      currentTable = null;
    }
  };

  lines.forEach((line) => {
    if (isTableLine(line)) {
      if (!currentTable) {
        flushText();
        currentTable = [];
      }
      currentTable.push(line);
    } else {
      if (currentTable) {
        flushTable();
      }
      currentTextLines.push(line);
    }
  });

  flushText();
  flushTable();

  return blocks;
};

const FormattedTable = ({ headers, rows }) => {
  if (!headers?.length && !rows?.length) {
    return null;
  }
  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {headers?.length > 0 && (
          <thead>
            <tr>
              {headers.map((header, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <th key={idx} className={styles.th}>
                  {renderFormattedInlineText(header)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        {rows?.length > 0 && (
          <tbody>
            {rows.map((row, rowIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={rowIdx} className={styles.tr}>
                {row.map((cell, cellIdx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={cellIdx} className={styles.td}>
                    {renderFormattedInlineText(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

const FormattedMessageText = ({ text, isError }) => {
  const blocks = useMemo(() => parseMessageText(text), [text]);

  return (
    <div
      className={getClassName(styles.messageText, {
        [styles.error]: isError,
      })}
    >
      {blocks.map((block, idx) => {
        if (block.type === "table") {
          return (
            <FormattedTable
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              headers={block.headers}
              rows={block.rows}
            />
          );
        }
        const trimmed = block.content.trim();
        if (!trimmed) {
          return null;
        }
        return (
          // eslint-disable-next-line react/no-array-index-key
          <p key={idx} className={styles.textParagraph}>
            {renderFormattedInlineText(block.content)}
          </p>
        );
      })}
    </div>
  );
};

const MessageBubble = ({ message }) => {
  if (message.role === "user") {
    return (
      <div className={styles.messageRowUser}>
        <div className={styles.messageUser}>{message.text}</div>
      </div>
    );
  }

  return (
    <div className={styles.messageAssistant}>
      <div className={styles.assistantLabel}>
        <CoolAssistantLogo size={20} />
        <span
          className={getClassName(styles.assistantLabelText, {
            [styles.error]: message.error,
          })}
        >
          CoolAssistant
        </span>
      </div>
      {message.text && (
        <FormattedMessageText text={message.text} isError={message.error} />
      )}
      {message.html && <SanitizedHtml html={message.html} />}
      <FileAttachments files={message.files} />
    </div>
  );
};

const ThinkingDots = () => (
  <div className={styles.thinkingDots}>
    <span className={styles.thinkingDot} />
    <span className={styles.thinkingDot} />
    <span className={styles.thinkingDot} />
  </div>
);

const ThinkingBubble = () => (
  <div className={styles.messageAssistant}>
    <div className={styles.assistantLabel}>
      <CoolAssistantLogo size={20} />
      <span className={styles.assistantLabelText}>CoolAssistant</span>
    </div>
    <ThinkingDots />
  </div>
);

/**
 * Chip above the input showing which sites the chat is scoped to, opening a checklist popover
 * to change it. An empty `selectedIds` means every site. Closes on an outside click or Escape.
 */
const SitePicker = ({ sites, selectedIds, onChange, maxSelected, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleMouseDown = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const atLimit = selectedIds.length >= maxSelected;

  const label = useMemo(() => {
    if (!selectedIds.length) {
      return "All sites";
    }
    if (selectedIds.length === 1) {
      const name = sites.find((site) => site.id === selectedIds[0])?.name;
      return name ? `Site: ${name}` : "Sites: 1";
    }
    return `Sites: ${selectedIds.length}`;
  }, [sites, selectedIds]);

  const visibleSites = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return sites;
    }
    return sites.filter((site) => site.name?.toLowerCase().includes(q));
  }, [sites, query]);

  const handleToggleSite = (siteId) => {
    if (selectedSet.has(siteId)) {
      onChange(selectedIds.filter((id) => id !== siteId));
      return;
    }
    if (atLimit) {
      return;
    }
    onChange([...selectedIds, siteId]);
  };

  return (
    <div className={styles.sitePicker} ref={rootRef}>
      <button
        type="button"
        className={styles.siteChip}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Sites this chat answers about"
      >
        <span className={styles.siteChipLabel}>{label}</span>
        <ChevronDown style={open ? { transform: "rotate(180deg)" } : undefined} />
      </button>

      {open && (
        <div className={styles.sitePopover} role="group" aria-label="Sites">
          {sites.length >= SITE_SEARCH_MIN_SITES && (
            <div className={styles.siteSearch}>
              <SearchInput placeholder="Search sites" value={query} onChange={setQuery} />
            </div>
          )}
          <div className={styles.siteList}>
            {!query.trim() && (
              <Checkbox
                className={styles.siteOption}
                checked={!selectedIds.length}
                onChange={() => onChange([])}
              >
                <span className={styles.siteOptionName}>All sites</span>
              </Checkbox>
            )}
            {visibleSites.map((site) => {
              const checked = selectedSet.has(site.id);
              return (
                <Checkbox
                  key={site.id}
                  className={styles.siteOption}
                  checked={checked}
                  value={site.id}
                  onChange={handleToggleSite}
                  disabled={!checked && atLimit}
                >
                  <span className={styles.siteOptionName}>{site.name}</span>
                </Checkbox>
              );
            })}
            {!visibleSites.length && <div className={styles.siteEmpty}>No matching sites</div>}
          </div>
          {atLimit && (
            <div className={styles.siteLimitHint}>Up to {maxSelected} sites can be selected</div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * @typedef {Object} CoolAssistantMessageContent
 * @property {string} [text] - Plain reply text.
 * @property {Array<{link: string, name: string, size?: string}>} [files] - File
 *   attachments, rendered one below another. Each is rendered as: an inline image if
 *   `name`/`link` end in a known image extension; a sandboxed (`allow-scripts` only, no
 *   same-origin/forms/top-navigation), script-capable `<iframe>` if `name`/`link` end in
 *   `.html`/`.htm` (e.g. a backend-rendered chart page, so it keeps real interactivity like
 *   hover tooltips); otherwise a card showing `name` and `size` that saves the file by
 *   fetching it and triggering a download from a blob URL — this forces a real save
 *   regardless of the link's origin, as long as it's CORS-readable (the plain
 *   `href`/`download` attributes stay as a fallback for right-click "Save Link As", and are
 *   what's used if the fetch itself fails, e.g. no CORS headers or a network error — in
 *   which case the browser just opens the file like a normal link). Because the iframe
 *   executes scripts, only ever point a file's `link` at HTML the host trusts (e.g.
 *   generated by its own backend).
 * @property {string} [html] - Raw HTML, sanitized before rendering. Since sanitization
 *   strips scripts/event handlers, this can't carry interactive content — use a `files`
 *   entry pointing at an `.html` page for that.
 */

/**
 * @typedef {Object} CoolAssistantRecentChat
 * @property {string} id - Identifies this chat to `onSelectChat`.
 * @property {string} title - Bold heading shown in the "Recents" list.
 * @property {string} [preview] - Muted one-line preview shown under the title, truncated
 *   with an ellipsis if it overflows.
 */

/**
 * Floating "CoolAssistant" AI assistant: a launcher button that expands into a chat panel.
 *
 * This component is presentation- and conversation-state only. It owns the open/closed
 * state, the message list, and history pagination, but delegates all I/O to the host app:
 * `onSend` submits a prompt to the host's backend, and the optional `getChatHistory` loads
 * prior messages (initially, and again — with a growing day window — as the user scrolls
 * up). Omit `getChatHistory` to start every session with an empty conversation.
 *
 * `getRecentChats`, `onNewChat` and `onSelectChat` are an independent, optional trio that
 * add multi-chat support: a menu icon (shown only if `getRecentChats` is provided) opens a
 * "Recents" panel in place of the conversation, and a compose icon (shown only if
 * `onNewChat` is provided) starts a fresh chat. Provide none of the three to keep the
 * widget single-chat, as before.
 *
 * @param {Object} props
 * @param {boolean} [props.defaultOpen=false] - Whether the panel is expanded on mount.
 * @param {string} [props.title='Control your sites with AI'] - Greeting headline shown
 *   before the first message.
 * @param {Array<string|{label: string, prompt: string}>} [props.suggestions=[]] - Quick-prompt
 *   chips shown alongside the greeting; clicking one sends it via `onSend` as if the user had
 *   typed it. Pass `{ label, prompt }` to show a short `label` on the chip but send the full
 *   `prompt`.
 * @param {(prompt: string) => Promise<string|CoolAssistantMessageContent>} props.onSend - Submits a
 *   user prompt to the host's backend and resolves with the assistant's reply — either a
 *   plain string, or a `{ text?, files?, html? }` object for replies carrying file
 *   attachments (each rendered inline if it looks like an image, otherwise as a download
 *   card) or sanitized HTML content. Required — the suggestion chips and send button are
 *   disabled while it is pending (the input stays editable), and a rejected promise renders
 *   as an error message bubble.
 * @param {(days: number) => Promise<Array<{id: string, role: 'user'|'assistant', text: string, files?: Array<{link: string, name: string, size?: string}>, html?: string}>>} [props.getChatHistory] -
 *   Fetches conversation history covering the last `days` days, newest-first-safe (any
 *   order is fine; ordering is derived from the returned array). Called once on mount with
 *   `initialHistoryDays`, and again with a progressively larger `days` window each time the
 *   user scrolls to the top of the conversation. Omit to disable history loading entirely.
 * @param {number} [props.initialHistoryDays=1] - Size, in days, of the history window
 *   requested on mount. Ignored if `getChatHistory` is not provided.
 * @param {number} [props.historyStepDays=1] - Additional days added to the history window
 *   on each "load more" fetch triggered by scrolling to the top.
 * @param {number} [props.loadMoreScrollThreshold=24] - Distance, in pixels, from the top of
 *   the message list within which scrolling triggers loading more history.
 * @param {() => Promise<CoolAssistantRecentChat[]>} [props.getRecentChats] - Fetches the
 *   list of recent chats (host decides the ordering and the 5-item cap) shown in the
 *   "Recents" panel. Called every time the menu icon is clicked. Omit to hide the menu icon
 *   entirely.
 * @param {() => (void|Promise<void>)} [props.onNewChat] - Starts a new chat: called when the
 *   compose icon is clicked (shown once a conversation has started, and always shown in the
 *   Recents panel). The widget clears its local message list immediately; this callback is
 *   where the host drops its current session so the next `onSend` starts a fresh one. Omit
 *   to hide the compose icon entirely.
 * @param {(id: string) => Promise<Array<{id: string, role: 'user'|'assistant', text: string, files?: Array<{link: string, name: string, size?: string}>, html?: string}>>} [props.onSelectChat] -
 *   Called when a "Recents" item is clicked, with that chat's `id`; resolves with its
 *   messages, which the widget renders in place of the current conversation. The host is
 *   responsible for switching its own active session to `id` before subsequent `onSend`
 *   calls.
 * @param {Array<{id: string, name: string}>} [props.sites] - Sites the chat can be scoped
 *   to. When non-empty, a chip above the input shows the current scope and opens a checklist
 *   to change it. Omit (or pass an empty array) to hide the chip.
 * @param {string[]} [props.selectedSiteIds=[]] - The chat's current site scope; empty means
 *   every site. Controlled — the host owns it and updates it from `onSelectedSiteIdsChange`
 *   (and from whatever scope its backend reports back).
 * @param {(ids: string[]) => void} [props.onSelectedSiteIdsChange] - Called with the new
 *   selection when the user changes it in the picker (`[]` for "All sites").
 * @param {number} [props.maxSelectedSites=20] - Cap on how many sites can be ticked at once.
 *   Pass a different value to override it.
 * @param {{right?: number, bottom?: number}} [props.position] - Offsets, in pixels, from
 *   the bottom-right corner of the viewport. Defaults to `{ right: 24, bottom: 24 }`
 *   (`16` on small screens); either key can be given alone, the other keeps its default.
 * @param {string} [props.className] - Extra class name(s) applied to the outermost element.
 * @param {import('react').CSSProperties} [props.style] - Inline styles applied to the
 *   outermost element, e.g. to override the default fixed positioning.
 * @returns {JSX.Element}
 */
const CoolAssistantWidget = ({
  defaultOpen = false,
  title = "Control your sites with AI",
  suggestions = [],
  onSend,
  getChatHistory,
  initialHistoryDays = 1,
  historyStepDays = 1,
  loadMoreScrollThreshold = 24,
  getRecentChats,
  onNewChat,
  onSelectChat,
  sites,
  selectedSiteIds = [],
  onSelectedSiteIdsChange,
  maxSelectedSites = 20,
  position,
  className: externalClassName,
  style,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [thinking, setThinking] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState(initialHistoryDays);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [view, setView] = useState("chat");
  const [recentChats, setRecentChats] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(false);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesInnerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!getChatHistory) {
      return;
    }
    let cancelled = false;
    stickToBottomRef.current = true;
    setView("chat");
    setMessages([]);
    setText("");
    setThinking(false);
    setHistoryLoading(true);
    setHistoryDays(initialHistoryDays);
    setHasMoreHistory(true);

    (async () => {
      try {
        const history = await getChatHistory(initialHistoryDays);
        if (!cancelled && history?.length) {
          setMessages(history);
        }
      } catch (err) {
        console.error("CoolAssistantWidget: failed to load chat history", err);
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getChatHistory, initialHistoryDays]);

  const started = messages.length > 0 || thinking;
  const messagesMounted = open && started && !historyLoading;

  useEffect(() => {
    if (!messagesMounted) {
      return;
    }
    const container = messagesContainerRef.current;
    const content = messagesInnerRef.current;
    if (!container || !content) {
      return;
    }
    stickToBottomRef.current = true;
    const stickToBottom = () => {
      if (stickToBottomRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    };
    stickToBottom();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(stickToBottom);
    observer.observe(content);
    return () => observer.disconnect();
  }, [messagesMounted]);

  const loadMoreHistory = useCallback(async () => {
    if (
      !getChatHistory ||
      loadingMoreRef.current ||
      historyLoading ||
      !hasMoreHistory
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextDays = historyDays + historyStepDays;
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    try {
      const history = await getChatHistory(nextDays);
      let addedCount = 0;
      setMessages((prev) => {
        const knownIds = new Set(prev.map((m) => m.id));
        const older = history.filter((m) => !knownIds.has(m.id));
        addedCount = older.length;
        if (older.length === 0) {
          return prev;
        }
        return [...older, ...prev];
      });

      if (addedCount > 0) {
        setHistoryDays(nextDays);
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          }
        });
      } else {
        setHasMoreHistory(false);
      }
    } catch (err) {
      console.error("CoolAssistantWidget: failed to load more chat history", err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [
    getChatHistory,
    historyLoading,
    hasMoreHistory,
    historyDays,
    historyStepDays,
  ]);

  const handleMessagesScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD;

      if (el.scrollTop <= loadMoreScrollThreshold) {
        loadMoreHistory();
      }
    },
    [loadMoreHistory, loadMoreScrollThreshold],
  );

  useEffect(() => {
    if (open && !thinking) {
      inputRef.current?.focus();
    }
  }, [open, thinking]);

  useEffect(() => {
    if (!open) {
      setView("chat");
    }
  }, [open]);

  const rootStyle = useMemo(
    () => ({
      "--coolassistant-widget-right": toPx(position?.right),
      "--coolassistant-widget-bottom": toPx(position?.bottom),
      ...style,
    }),
    [position?.right, position?.bottom, style],
  );

  const send = useCallback(
    async (prompt) => {
      const trimmed = prompt.trim();
      if (!trimmed || thinking || !onSend) {
        return;
      }

      stickToBottomRef.current = true;
      setMessages((prev) => [
        ...prev,
        { id: nextId++, role: "user", text: trimmed },
      ]);
      setText("");
      setThinking(true);

      try {
        const reply = await onSend(trimmed);
        const assistantMessage =
          typeof reply === "string"
            ? { id: nextId++, role: "assistant", text: reply }
            : {
                id: nextId++,
                role: "assistant",
                text: reply?.text,
                files: reply?.files,
                html: reply?.html,
              };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextId++,
            role: "assistant",
            text: err?.message ?? "Something went wrong.",
            error: true,
          },
        ]);
      } finally {
        setThinking(false);
      }
    },
    [thinking, onSend],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(text);
    }
  };

  const handleToggleOpen = () => {
    setOpen((o) => !o);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenRecents = useCallback(async () => {
    if (!getRecentChats || thinking) {
      return;
    }
    setView("recents");
    setRecentsLoading(true);
    try {
      const chats = await getRecentChats();
      setRecentChats(chats ?? []);
    } catch (err) {
      console.error("CoolAssistantWidget: failed to load recent chats", err);
      setRecentChats([]);
    } finally {
      setRecentsLoading(false);
    }
  }, [getRecentChats, thinking]);

  const handleBackFromRecents = useCallback(() => {
    setView("chat");
  }, []);

  const handleNewChat = useCallback(async () => {
    if (thinking) {
      return;
    }
    stickToBottomRef.current = true;
    setView("chat");
    setMessages([]);
    setText("");
    try {
      await onNewChat?.();
    } catch (err) {
      console.error("CoolAssistantWidget: failed to start a new chat", err);
    }
  }, [thinking, onNewChat]);

  const handleSelectChat = useCallback(
    async (id) => {
      if (!onSelectChat) {
        return;
      }
      stickToBottomRef.current = true;
      setView("chat");
      setThinking(false);
      setHistoryLoading(true);
      try {
        const history = await onSelectChat(id);
        setMessages(history ?? []);
      } catch (err) {
        console.error("CoolAssistantWidget: failed to load selected chat", err);
      } finally {
        setHistoryLoading(false);
      }
    },
    [onSelectChat],
  );

  const showNewChatButton = Boolean(onNewChat) && (view === "recents" || started);

  const handleRecentsItemClick = useCallback(
    (e) => {
      handleSelectChat(e.currentTarget.dataset.chatId);
    },
    [handleSelectChat],
  );

  const renderRecentsPanel = () => (
    <div className={styles.recents}>
      <div className={styles.recentsLabel}>Recents</div>
      {recentsLoading ? (
        <div className={styles.recentsLoading}>
          <ThinkingDots />
        </div>
      ) : !recentChats.length ? (
        <div className={styles.recentsEmpty}>
          <span className={styles.recentsEmptyIcon}>
            <ChatBubbleIcon size={22} color="#a3a3a3" />
          </span>
          <span className={styles.recentsEmptyTitle}>No conversations yet</span>
          <span className={styles.recentsEmptyHint}>
            Start a new chat and it will show up here
          </span>
        </div>
      ) : (
        <div className={styles.recentsList}>
          {recentChats.map((chat) => (
            <button
              key={chat.id}
              type="button"
              className={styles.recentsItem}
              data-chat-id={chat.id}
              onClick={handleRecentsItemClick}
            >
              <span className={styles.recentsItemTitle}>{chat.title}</span>
              {chat.preview && (
                <span className={styles.recentsItemPreview}>{chat.preview}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderPanelBody = () => {
    if (view === "recents") {
      return renderRecentsPanel();
    }
    if (historyLoading) {
      return (
        <div className={styles.historyLoading}>
          <ThinkingDots />
        </div>
      );
    }
    if (started) {
      return (
        <div
          className={styles.messages}
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
          <div className={styles.messagesInner} ref={messagesInnerRef}>
            {loadingMore && (
              <div className={styles.loadingMore}>
                <ThinkingDots />
              </div>
            )}
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {thinking && <ThinkingBubble />}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.greeting}>
        <div className={styles.greetingContent}>
          <p className={styles.title}>{title}</p>
          <div className={styles.chips}>
            {suggestions.map((s) => {
              const { label, prompt } = typeof s === "string" ? { label: s, prompt: s } : s;
              return (
                <button
                  key={label}
                  type="button"
                  className={styles.chip}
                  onClick={() => send(prompt)}
                  disabled={thinking}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={getClassName(styles.root, externalClassName)}
      style={rootStyle}
    >
      {open && (
        <div className={styles.widget}>
          {view === "chat" && !historyLoading && !started && (
            <div className={styles.greetingBlob} />
          )}

          <div className={styles.header}>
            <div className={styles.headerSide}>
              {view === "recents" ? (
                <IconButton
                  variant="text"
                  className={styles.headerIconButton}
                  onClick={handleBackFromRecents}
                  icon={<ChevronLeft width={20} height={20} />}
                  title="Back"
                />
              ) : (
                getRecentChats && (
                  <IconButton
                    variant="text"
                    className={styles.headerIconButton}
                    onClick={handleOpenRecents}
                    disabled={thinking}
                    icon={<MenuIcon size={20} />}
                    title="Recent chats"
                  />
                )
              )}
            </div>

            {view === "chat" && (
              <div className={styles.brand}>
                <CoolAssistantLogo size={20} />
                <span className={styles.brandName}>CoolAssistant</span>
              </div>
            )}

            <div className={getClassName(styles.headerSide, styles.headerSideEnd)}>
              {showNewChatButton && (
                <IconButton
                  variant="text"
                  className={styles.headerIconButton}
                  onClick={handleNewChat}
                  disabled={thinking}
                  icon={<EditIcon size={20} />}
                  title="New conversation"
                />
              )}
              <IconButton
                variant="text"
                className={styles.headerIconButton}
                onClick={handleClose}
                icon={<CloseIcon width={20} height={20} />}
                title="Close"
              />
            </div>
          </div>

          {renderPanelBody()}

          {view === "chat" && (
            <div className={styles.footer}>
              {sites?.length > 0 && onSelectedSiteIdsChange && (
                <SitePicker
                  sites={sites}
                  selectedIds={selectedSiteIds}
                  onChange={onSelectedSiteIdsChange}
                  maxSelected={maxSelectedSites}
                  disabled={thinking || historyLoading}
                />
              )}
              <div className={styles.inputBar}>
                <input
                  ref={inputRef}
                  className={styles.input}
                  placeholder="Ask anything"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={historyLoading}
                />
                {!(thinking || historyLoading || !text.trim()) && (
                  <IconButton
                    variant="text"
                    size="small"
                    className={styles.sendButton}
                    onClick={() => send(text)}
                    icon={<SendIcon size={20} color="#fff" />}
                    title="Send"
                  />
                )}
              </div>
              <p className={styles.caption}>AI might make mistakes, please validate accuracy</p>
            </div>
          )}
        </div>
      )}

      <IconButton onClick={handleToggleOpen} className={styles.launcher}>
        {open ? <CoolAssistantMarkLight size={64} /> : <CoolAssistantMark size={64} />}
      </IconButton>
    </div>
  );
};

export default CoolAssistantWidget;
